import { PageLoader } from '@/components/PageLoader';
import { useState } from 'react';
import { Switch } from 'antd';

import {
  inspectionsKey,
  pendingInspectionsKey,
  useGarmentInspection,
  usePendingInspections,
  type PendingInspection,
  type ReturnInspection,
} from '@/api/inspections';
import { useApiMutation, submitVia } from '@/api/mutations';
import { bookingsKey } from '@/api/bookings';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import type { Session } from '@core/session/types';
import { InspectionPage } from './returns/InspectionPage';
import type { toRequest } from '@core/returns/inspection';
import { saveInspection } from '@core/api/inspectionCommands';

/**
 * Section 9's return counter.
 *
 * <p>
 * Two screens in one key, the arrangement `BookingsPage` established: the queue is the list, and
 * inspecting one garment is a full page held as internal state. The shell navigates by key and has
 * no router, so a sub-screen cannot have an address of its own — which does mean a refresh mid
 * inspection lands back on the queue. Nothing is lost by that: an inspection is saved in one call.
 * </p>
 *
 * <p>
 * This page owns the queries and the mutation and nothing else; <c>InspectionPage</c> is
 * presentational and takes a view plus an intent, exactly as the two booking counters do.
 * </p>
 */
export function ReturnsPage({ session }: { session: Session }) {
  const [includeInspected, setIncludeInspected] = useState(false);
  const [inspecting, setInspecting] = useState<number | null>(null);

  const pending = usePendingInspections(includeInspected);
  const view = useGarmentInspection(inspecting);

  const save = useApiMutation<ReturnInspection, ReturnType<typeof toRequest>>({
    // The one definition both clients send (`@core/api/inspectionCommands`).
    mutationFn: (payload) => saveInspection(inspecting ?? 0, payload),
    success: 'Inspection recorded.',
    // The booking's own view carries the damage charge this writes, so a stale register would show
    // a garment as inspected and owing nothing.
    invalidates: [pendingInspectionsKey, inspectionsKey, bookingsKey],
    onSuccess: () => setInspecting(null),
  });

  if (inspecting !== null) {
    if (view.isLoading || !view.data) {
      return (
        <Card>
          <PageLoader label="Loading the return" />
        </Card>
      );
    }

    return (
      <InspectionPage
        view={view.data}
        saving={save.isPending}
        onSave={(payload) => void submitVia(save, payload)}
        onBack={() => setInspecting(null)}
      />
    );
  }

  const columns: DataViewColumn<PendingInspection>[] = [
    {
      title: 'Garment',
      dataIndex: 'garmentName',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.garmentName}</div>
          <div className="font-mono-nums text-[13px] text-default">
            {row.serialCode}
            {row.bookingNumber ? ` · ${row.bookingNumber}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      render: (_, row) => (
        <div>
          <div className="text-sm text-gray-900">{row.customerName}</div>
          {row.wearerName && row.wearerName !== row.customerName && (
            <div className="text-[13px] text-default">worn by {row.wearerName}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Came back',
      dataIndex: 'returnedOn',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {row.returnedOn ? formatDate(row.returnedOn) : '—'}
        </span>
      ),
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      render: (_, row) =>
        row.inspectedOn === null ? (
          <StatusBadge tone="warning">Waiting</StatusBadge>
        ) : row.outcome === 'BeyondRepair' ? (
          <StatusBadge tone="negative">Written off</StatusBadge>
        ) : row.outcome === 'Repairable' ? (
          <StatusBadge tone="warning">Repairable</StatusBadge>
        ) : (
          <StatusBadge tone="positive">Clean</StatusBadge>
        ),
    },
    {
      title: 'Charge',
      dataIndex: 'damageCharge',
      align: 'right',
      render: (_, row) => (
        <div>
          <span className="font-mono-nums text-[13px]">
            {/* Null means two different things, and only `inspectedOn` tells them apart: a garment
                nobody has looked at yet, and one looked at with nothing owed. A dash for both would
                read as "no charge" on a garment that has not been inspected.

                A third case sits under the first: a charge over the approval threshold is recorded
                and deliberately kept off the booking, so `damageCharge` is empty while the
                inspection came to a real figure. Reading only that column would tell a clerk a
                garment assessed at ₹5,000 owes nothing — which is what they would then tell the
                customer. The assessed figure is what is shown, with the badge saying it is not
                billed yet. */}
            {row.inspectedOn === null
              ? '—'
              : formatAmount(row.assessedCharge ?? row.damageCharge ?? 0)}
          </span>
          {row.approvalState === 'Pending' && (
            <div className="mt-0.5">
              <StatusBadge tone="warning">Not billed — awaiting approval</StatusBadge>
            </div>
          )}
          {row.approvalState === 'Rejected' && (
            <div className="mt-0.5">
              <StatusBadge tone="neutral">Refused — not billed</StatusBadge>
            </div>
          )}
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'rentalOrderItemId',
      trailing: true,
      // Reviewing is a read and stays open to anyone who may see the screen; only recording one is
      // gated. WriteAction hands the control its own disabled state and the reason, rather than each
      // screen inventing a tooltip.
      render: (_, row) =>
        row.inspectedOn === null ? (
          <WriteAction session={session} required="ReturnInspection:Edit">
            {(disabled) => (
              <Button
                icon="icon icon-clipboard-check"
                disabled={disabled}
                onClick={() => setInspecting(row.rentalOrderItemId)}
              >
                Inspect
              </Button>
            )}
          </WriteAction>
        ) : (
          <Button
            icon="icon icon-clipboard-check"
            onClick={() => setInspecting(row.rentalOrderItemId)}
          >
            Review
          </Button>
        ),
    },
  ];

  const waiting = (pending.data ?? []).filter((row) => row.inspectedOn === null).length;

  // Only visible with "show inspected" on, which is correct: a held-back charge belongs to a
  // garment that *has* been looked at, and the default view is the ones that have not.
  const heldBack = (pending.data ?? []).filter((row) => row.approvalState === 'Pending').length;

  return (
    <>
      <PageHeading
        title="Returns & inspection"
        subtitle="What has come back, and what it cost. The most disputed moment in the business — so it is the one with a record behind it."
      />

      {heldBack > 0 && (
        <div className="mb-3 lg:mb-6">
          <Notice
            tone="warning"
            title={`${heldBack} charge${heldBack === 1 ? '' : 's'} waiting on a signature`}
          >
            These were assessed and are on nobody&rsquo;s bill — the deposit behind each settles as
            though the garment came back clean. They clear from Charge approvals.
          </Notice>
        </div>
      )}

      {waiting > 0 && (
        <div className="mb-3 lg:mb-6">
          <Notice tone="info" title={`${waiting} garment${waiting === 1 ? '' : 's'} waiting`}>
            A garment stays on this list until it has been looked at. Until then its damage charge
            is nothing, and the deposit settles as though it came back clean.
          </Notice>
        </div>
      )}

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-default sm:ms-auto">
            <Switch size="small" checked={includeInspected} onChange={setIncludeInspected} />
            Show inspected
          </label>
        </div>

        <DataView<PendingInspection>
          columns={columns}
          dataSource={pending.data ?? []}
          loading={pending.isLoading}
          rowKey="rentalOrderItemId"
          empty={
            <EmptyState
              icon="icon icon-clipboard-check"
              title={includeInspected ? 'Nothing has come back yet' : 'Nothing waiting'}
              hint={
                includeInspected
                  ? 'Garments appear here once a booking takes them back.'
                  : 'Every garment that has come back has been looked at. Turn on “show inspected” to see them.'
              }
            />
          }
        />
      </Card>
    </>
  );
}
